import React from 'react';

import { Box, Checkbox, Group, Paper, Title, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import EventTypeMultiSelectAutoComplete from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import StaffMultiSelectAutoComplete from 'components/form-fields/auto-complete-fields/StaffMultiSelectAutoComplete';
import { EventType } from 'types';
import { WeekdayType } from 'utils/weekdays';

import ClearFilters from './ClearFilters';
import DownloadCSV from './DownloadCSV';
import GoBackButton from './GoBackButton';
import TimeRangePickerFilter from './TimePickerFilter';
import useStyles from './useStyles';
import WeekdayFilter from './WeekdayFilter';
import { ClassUsageSearchFilters } from '../../SearchFilters';
import DateRangePicker from '../DateRangePicker';

type GroupByFilters = Pick<
  ClassUsageSearchFilters,
  'groupByClass' | 'groupByDay' | 'groupByStartTime' | 'groupByCoach'
>;

const ClassUsagePageHeader = ({
  searchFilters,
  setSearchFilters,
  initialSearchFiltersValue,
}: {
  searchFilters: ClassUsageSearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<ClassUsageSearchFilters>>;
  initialSearchFiltersValue: ClassUsageSearchFilters;
}): JSX.Element => {
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`, false);
  const { classes } = useStyles();

  const handleGroupBy = (key: keyof GroupByFilters) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = !!e.currentTarget?.checked;
    setSearchFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleWeekdaysChange = (values: WeekdayType['day'][]): void => {
    setSearchFilters(prev => ({ ...prev, weekdays: values }));
  };

  const handleCoachesChange = (values: string[]): void => {
    setSearchFilters(prev => ({ ...prev, coachIds: values }));
  };

  const handleClassTypesChange = (values: EventType[]): void => {
    setSearchFilters(prev => ({ ...prev, classTypes: values }));
  };

  const handleTimeRangeChange = (value: [string | null, string | null]): void => {
    setSearchFilters(prev => ({ ...prev, classStartTimeFrom: value[0], classStartTimeTo: value[1] }));
  };

  return (
    <Paper className={classes.header}>
      <Group noWrap spacing='xl' className={classes.title} data-cy='classUsagePageHeader'>
        <GoBackButton />
        <Title order={isDesktop ? 1 : 3}>Class Usage</Title>
      </Group>
      <DateRangePicker
        className={classes.filterPeriod}
        value={{
          period: searchFilters.period,
          startDate: searchFilters.startDate,
          endDate: searchFilters.endDate,
        }}
        onChange={v =>
          setSearchFilters(prev => ({
            ...prev,
            period: v.period,
            startDate: v.startDate,
            endDate: v.endDate,
          }))
        }
      />
      <Box className={classes.filters}>
        <EventTypeMultiSelectAutoComplete
          placeholder='Class Name'
          handleChange={handleClassTypesChange}
          values={searchFilters.classTypes}
          className={classes.filterClassType}
        />
        <WeekdayFilter
          placeholder='Week Day'
          value={searchFilters.weekdays}
          onChange={handleWeekdaysChange}
          className={classes.filterWeekday}
        />
        <TimeRangePickerFilter
          placeholder='Class Start Time'
          value={[searchFilters.classStartTimeFrom, searchFilters.classStartTimeTo]}
          onChange={handleTimeRangeChange}
          className={classes.filterTimeRange}
        />
        <StaffMultiSelectAutoComplete
          placeholder='Coach'
          onChange={handleCoachesChange}
          value={searchFilters.coachIds}
          isCoach
          showNoStaff={false}
          className={classes.filterCoach}
        />
        <Checkbox
          label='Group by class'
          className={classes.filterGroupByClass}
          checked={searchFilters.groupByClass}
          onChange={handleGroupBy('groupByClass')}
        />
        <Checkbox
          label='Group by day'
          className={classes.filterGroupByDay}
          checked={searchFilters.groupByDay}
          onChange={handleGroupBy('groupByDay')}
        />
        <Checkbox
          label='Group by start time'
          className={classes.filterGroupByStartTime}
          checked={searchFilters.groupByStartTime}
          onChange={handleGroupBy('groupByStartTime')}
        />
        <Checkbox
          label='Group by coach'
          className={classes.filterGroupByCoach}
          checked={searchFilters.groupByCoach}
          onChange={handleGroupBy('groupByCoach')}
        />
      </Box>
      <ClearFilters
        className={classes.clear}
        setSearchFilters={setSearchFilters}
        initialSearchFiltersValue={initialSearchFiltersValue}
      />
      <DownloadCSV className={classes.download} searchFilters={searchFilters} />
    </Paper>
  );
};

export default ClassUsagePageHeader;
